import client from './client';

/** Один вызов инструмента внутри ответа: с чем позвали и что вернулось. */
export interface ToolCall {
  tool: string;
  args?: Record<string, any>;
  ms?: number;
  result?: any;
  /** Заполнено, если инструмент позвала не модель, а страховка. */
  via?: string;
}

/** Строка лога Айланы в едином виде (сервер сводит две формы дневника в одну). */
export interface AssistantLogRow {
  id: number;
  created_at: string;
  session_id: string | null;
  user_id: string | null;
  user_name: string | null;
  message: string;
  answer: string | null;
  error: string | null;
  feedback: number | null;
  latency_ms: number | null;
  iterations: number | null;
  tools: string[];
  calls: ToolCall[];
  tokens: { prompt?: number; completion?: number; cache_hit?: number; cache_miss?: number } | null;
  llm_ms: number | null;
  tools_ms: number | null;
  cache_hit: boolean;
  safety_nets: string[];
  history_len: number | null;
  lang: string | null;
  geo: boolean | null;
  stream: boolean | null;
  cards: number;
  entities: number;
  /** 0 — старый плоский формат (имена инструментов без аргументов). */
  details_v: number;
}

export interface ReviewMark {
  id: number;
  checked_at: string;
  last_log_id: number;
  count: number | null;
  admin_name: string | null;
  note: string | null;
}

export interface Overview {
  total_all: number;
  since_log_id: number;
  last_review: ReviewMark | null;
  fresh: {
    count: number;
    errors: number;
    thumbs_down: number;
    thumbs_up: number;
    slow: number;
    no_tools: number;
    sessions: number;
    users: number;
    first_at: string | null;
    last_at: string | null;
    max_log_id: number | null;
    p50_ms: number | null;
    p95_ms: number | null;
    tools: { name: string; n: number }[];
  };
  reviews: ReviewMark[];
  slow_ms: number;
}

/** Находка сторожа + строка лога, к которой она относится. */
export interface DefectRow {
  id: number;
  created_at: string;
  log_id: number;
  detector: string;
  severity: 'high' | 'normal';
  surface: string | null;
  excerpt: string | null;
  dismissed_at: string | null;
  log_at: string;
  message: string;
  answer: string | null;
  session_id: string | null;
}

export interface DefectsQuery {
  detector?: string;
  severity?: 'high' | 'normal';
  surface?: string;
  show_dismissed?: boolean;
  limit?: number;
  offset?: number;
}

export interface DefectsResponse {
  total: number;
  items: DefectRow[];
  by_detector: { detector: string; severity: string; count: number }[];
}

export interface ScanResult {
  scanned: number;
  found: number;
  by_detector: Record<string, number>;
}

export type OnlyFilter = 'errors' | 'down' | 'up' | 'rated' | 'slow' | 'no_tools' | 'cache';

export interface LogsQuery {
  since_last_review?: boolean;
  since_id?: number;
  date_from?: string;
  date_to?: string;
  only?: OnlyFilter;
  tool?: string;
  q?: string;
  session_id?: string;
  limit?: number;
  offset?: number;
}

export interface LogsResponse {
  items: AssistantLogRow[];
  total: number;
  since_id: number | null;
}

export interface LogDetail {
  item: AssistantLogRow;
  session: AssistantLogRow[];
  /** true — цепочка СОБРАНА ПО ДОГАДКЕ (сосед по времени), а не по id разговора. */
  session_guessed: boolean;
}

/** Ответ выгрузки: сам файл + сколько строк в него вошло. */
export interface ExportResult {
  blob: Blob;
  filename: string;
  count: number;
  lastId: number;
}

export const assistantApi = {
  overview: async (): Promise<Overview> => {
    const { data } = await client.get('/api/admin/assistant/overview');
    return data;
  },
  logs: async (params: LogsQuery): Promise<LogsResponse> => {
    const { data } = await client.get('/api/admin/assistant/logs', { params });
    return data;
  },
  detail: async (id: number): Promise<LogDetail> => {
    const { data } = await client.get(`/api/admin/assistant/logs/${id}`);
    return data;
  },
  /**
   * Выгрузка файлом. `mark` ставит отметку проверки — но ТОЛЬКО если в файл
   * что-то вошло (это правило на сервере: пустая выгрузка курсор не двигает).
   */
  export: async (
    params: LogsQuery & { fmt: 'md' | 'jsonl'; mark?: boolean },
  ): Promise<ExportResult> => {
    const res = await client.get('/api/admin/assistant/export', {
      params,
      responseType: 'blob',
    });
    const cd = String(res.headers['content-disposition'] || '');
    const m = /filename="?([^"]+)"?/.exec(cd);
    return {
      blob: res.data as Blob,
      filename: m ? m[1] : `ailana.${params.fmt}`,
      count: Number(res.headers['x-export-count'] || 0),
      lastId: Number(res.headers['x-export-last-id'] || 0),
    };
  },
  mark: async (last_log_id: number, count?: number, note?: string) => {
    const { data } = await client.post('/api/admin/assistant/reviews', {
      last_log_id, count, note,
    });
    return data as { id: number; checked_at: string; last_log_id: number };
  },
  dropMark: async (id: number) => {
    await client.delete(`/api/admin/assistant/reviews/${id}`);
  },

  /**
   * СТОРОЖ: находки ночного разбора. Список сам по себе бесполезен, если его
   * не чистить, — поэтому рядом всегда `dismiss`: отметки «не дефект»
   * показывают, какой детектор шумит и какой порог пора ужесточать.
   */
  defects: async (params: DefectsQuery = {}): Promise<DefectsResponse> => {
    const { data } = await client.get('/api/admin/assistant/defects', { params });
    return data;
  },
  dismissDefect: async (id: number, dismissed: boolean) => {
    await client.post(`/api/admin/assistant/defects/${id}/dismiss`, { dismissed });
  },
  scanDefects: async (days = 1): Promise<ScanResult> => {
    const { data } = await client.post('/api/admin/assistant/defects/scan', null, {
      params: { days },
    });
    return data;
  },
};
